// src/api/authApi.ts
import apiClient from '../api/apiClient';
import {AuthBackDepositorTypes} from "../types/common/bank.account.types";

interface TokenResponse {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
}

interface BankDto {
    bankCode: string;
    bankName: string;
}

interface AreaDto {
    areaCode: string;
    areaName: string;
    upperCoCode?: string;
}

export interface MemberDto {
    memberId: string;
    password: string;
    memberName: string;
    phone: string;
    bankCode?: string;
    accountNumber?: string;
    paymentPassword?: string;
    fcmToken?: string | null;
    deviceType?: "ios" | "android" | null;
    nowPassword: string;
    newPassword: string;
    deviceId?: string | null;
}

interface VerificationDto {
    memberName?: string;
    phone: string;
    code?: string;
}

interface ResponseDto<T> {
    code: string;
    data: T;
    message?: string;
}
export interface VerifyPasswordDto {
    password: string;
}



// ==================== 회원 인증 API ====================

/**
 * 회원가입
 */
export const signup = (data: MemberDto) =>
    apiClient.post<ResponseDto<TokenResponse>>(`/api/front/auth/signup`, data);

/**
 * 로그인
 */
export const login = (data: Pick<MemberDto, 'memberId' | 'password' | 'fcmToken' | 'deviceType' | 'deviceId'>) =>
    apiClient.post<ResponseDto<TokenResponse>>(`/api/front/auth/login`, data);

// ==================== 코드 조회 API ====================

/**
 * 은행 목록 조회
 */
export const getBankList = () =>
    apiClient.get<ResponseDto<BankDto[]>>(`/api/front/auth/banks`);

/**
 * 지역 목록 조회
 */
export const getAreaList = () =>
    apiClient.get<ResponseDto<AreaDto[]>>(`/api/front/auth/areas`);

/**
 * 하위 지역 목록 조회
 */
export const findAreaList = (upperCoCode: string) =>
    apiClient.get<ResponseDto<AreaDto[]>>(`/api/front/auth/areas/sub`, {
        params: { upperCoCode }
    });

/**
 * 아이디 저장 체크 시 조회
 */
export const getMemberIdByDeviceId = (deviceId: string | null) =>
    apiClient.get<ResponseDto<string>>(`/api/front/auth/getMemberIdByDeviceId`, {
        params: { deviceId }
    });

// ==================== 유효성 검증 API ====================

/**
 * 아이디 중복 검증
 * @returns true: 사용 가능, false: 중복
 */
export const validateId = (id: string) =>
    apiClient.get<ResponseDto<boolean>>(`/api/front/auth/validate-id`, {
        params: { id }
    });

// ==================== 인증 코드 API ====================

/**
 * 인증 코드 발송
 */
export const sendVerificationCode = (data: Pick<VerificationDto, 'memberName' | 'phone'>) =>
    apiClient.post<ResponseDto<void>>(`/api/front/auth/send-code`, data);

/**
 * 인증 코드 검증
 */
export const verifyCode = (data: Pick<VerificationDto, 'phone' | 'code'>) =>
    apiClient.post<ResponseDto<boolean>>(`/api/front/auth/verify-code`, data);


/**
 * 아이디 찾기
 */
export const findId = (data: Pick<VerificationDto, 'memberName' | 'phone' | 'code'>) =>
    apiClient.post<ResponseDto<string>>(`/api/front/auth/find-id`, data);

/**
 * 비밀번호 재설정
 */
export const resetPassword = (data: {memberId: string; phone: string; newPassword: string}) =>
    apiClient.post<ResponseDto<void>>(`/api/front/auth/reset-password`, data);

// ==================== 유틸리티 함수 ====================

/**
 * 토큰 응답 처리
 */
export const handleTokenResponse = (response: ResponseDto<TokenResponse>) => {
    const { accessToken, refreshToken, expiresIn } = response.data;
    const expireTime = Date.now() + 1000 * 60 * expiresIn;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem("tokenExpire", String(expireTime));
    localStorage.setItem("expiresIn", String(expiresIn));
    return response.data;
};

/**
 * 토큰 제거
 */
export const clearTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpire');
    localStorage.removeItem("expiresIn");
};

export const isTokenValid = () => {
    const expire = localStorage.getItem("tokenExpire");
    if (!expire) return false;
    return Date.now() < Number(expire);
};

export const checkDepositor = (data: AuthBackDepositorTypes) =>
    apiClient.get<ResponseDto<any>>("/api/front/auth/checkDepositor",
        {
            params: data,
            headers: {'Content-Type': 'application/json'}
        }
    );
/**
 * 로그인 비밀번호변경알림 - 다음에 변경
 */
export const changePasswordAfter = (data: Pick<MemberDto, 'memberId'>) =>
    apiClient.post<ResponseDto<any>>(`/api/front/auth/changePasswordAfter`, data);

/**
 * 로그인 비밀번호변경알림 - 새 비밀번호 저장
 */
export const changePasswordSubmit = (data: Pick<MemberDto, 'memberId' | 'nowPassword' | 'newPassword'>) =>
    apiClient.post<ResponseDto<any>>(`/api/front/auth/changePasswordSubmit`, data);

/**
 * 코디 승인 후 role 업그레이드 (ROLE_MEMBER → ROLE_COORDINATOR 토큰 재발급)
 * - 코디 메뉴 진입 시 상태가 COMPLETE이고 현재 role이 ROLE_MEMBER인 경우 호출
 * - 성공 시 반환된 토큰으로 handleTokenResponse() 호출하여 localStorage 갱신
 */
export const upgradeToCoordinator = () =>
    apiClient.post<ResponseDto<TokenResponse>>(`/api/front/auth/upgrade-to-coordinator`);